import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderOpen, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { listCollections, createCollection, deleteCollection, ApiError, type Collection } from '../../features/collections/api';

/**
 * Collections in the selected workspace.
 *
 * A page, NOT a second header selector. The workspace selector already governs
 * every view; a second global dropdown would leave two things silently deciding
 * what you are looking at.
 */
/** The stored description is sanitised HTML (the community shell writes rich text); this page shows it as text. */
function asText(html: string): string {
  return (new DOMParser().parseFromString(html, 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim();
}

export function CollectionsPage() {
  const { current, ready } = useSelectedWorkspace();
  const workspace = current?.public_id;
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['collections', workspace],
    queryFn: () => listCollections(workspace),
    enabled: ready,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['collections', workspace] });
  const fail = (e: unknown) => setError(e instanceof ApiError ? e.message : 'Something went wrong.');

  const create = useMutation({
    mutationFn: () => createCollection(name.trim(), workspace),
    onSuccess: () => { setName(''); setError(null); invalidate(); },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: deleteCollection,
    onSuccess: () => { setError(null); invalidate(); },
    onError: fail,
  });

  const collections = data?.collections ?? [];
  /**
   * Whether this person may CHANGE this workspace: an admin or editor, per the
   * server. One boolean for the page, because the page is one workspace.
   *
   * `=== true`, so the state before the first response — and any answer from a
   * server that does not send the field — reads as "no". Controls appear once
   * permission is known, never while it is merely unrefuted.
   */
  const editable = data?.editable === true;

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      {editable && (
        <div className="flex items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">New collection</label>
            <Input
              value={name}
              placeholder="Teaching set"
              className="w-72"
              onChange={(e) => setName(e.currentTarget.value)}
              onKeyDown={(e) => e.key === 'Enter' && name.trim() && create.mutate()}
            />
          </div>
          <Button disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
            <Plus className="mr-1 h-4 w-4" /> Create
          </Button>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {isLoading || !ready ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : collections.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {editable
            ? 'No collections yet. A collection groups slides however you like — a teaching set, a cohort, a batch to review.'
            : 'No collections in this workspace yet.'}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {collections.map((c) => (
            <Row
              key={c.public_id}
              c={c}
              // Undefined for a viewer, which is what removes the button: the
              // row renders what can be done, not what would be refused.
              onDelete={!editable ? undefined : () => {
                // Irreversible destruction of imaging data, so the count is
                // stated before it happens. Not friction for its own sake: the
                // number is the only thing that distinguishes deleting an empty
                // shelf from deleting a term's worth of teaching slides.
                const what = c.studies === 0
                  ? `Delete "${c.name}"?`
                  : `Delete "${c.name}" and its ${c.studies} ${c.studies === 1 ? 'slide' : 'slides'}?\n\n`
                    + 'The slides and their images are permanently deleted. This cannot be undone.\n\n'
                    + 'To keep them, cancel and move them to another collection first.';
                if (window.confirm(what)) remove.mutate(c.public_id);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ c, onDelete }: { c: Collection; onDelete?: () => void }) {
  return (
    <div className="flex items-center gap-3 rounded-md border p-3">
      <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <Link to={`/collections/${c.public_id}`} className="font-medium hover:underline">
          {c.name}
        </Link>
        {c.description && <p className="truncate text-sm text-muted-foreground">{asText(c.description)}</p>}
      </div>
      {/* Slides, not instances: a slide is the unit a person counts. */}
      <Badge variant="secondary">{c.studies} {c.studies === 1 ? 'slide' : 'slides'}</Badge>
      {c.groups > 0 && <Badge variant="outline">{c.groups} {c.groups === 1 ? 'group' : 'groups'}</Badge>}
      {/* Delete deletes, and it takes the slides with it. The confirmation
          above states the count because that is the only thing separating
          "remove an empty shelf" from "destroy a term of teaching slides". */}
      {onDelete && (
        <Button variant="ghost" size="sm" onClick={onDelete} title="Delete">
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
